// Petsmate formulary — Part A (50 conditions)
// Schema:
// {
//   id, name, species: ["canine"|"feline"], category, clinicalSigns,
//   diagnostics: [{ test, finding }],
//   differentials: [ "..." ],
//   medications: [{ drug, doseMgPerKg, doseText, route, frequency, duration, notes? }],
//   supportiveCare: [ "..." ]
// }

export const DISEASES_A = [
  // ==================== GASTROINTESTINAL ====================
  {
    id: "gastroenteritis",
    name: "Acute Gastroenteritis",
    species: ["canine", "feline"],
    category: "Gastrointestinal",
    clinicalSigns: "Vomiting, diarrhoea, inappetence, mild dehydration.",
    diagnostics: [
      { test: "History & physical exam", finding: "Dietary indiscretion, mild dehydration, abdominal discomfort" },
      { test: "CBC", finding: "Usually normal or mild stress leukogram" },
      { test: "Faecal flotation + Parvo/Giardia SNAP", finding: "Rule out parasites & viral enteritis" },
      { test: "Basic biochem + electrolytes", finding: "Hypokalaemia possible with vomiting" },
    ],
    differentials: ["Parvoviral enteritis", "Foreign body obstruction", "Pancreatitis", "IBD flare", "Toxin ingestion"],
    medications: [
      { drug: "Maropitant citrate (Cerenia)", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC / PO", frequency: "SID", duration: "3–5 days" },
      { drug: "Metronidazole", doseMgPerKg: 15, doseText: "15 mg/kg", route: "PO", frequency: "BID", duration: "5–7 days", notes: "Give with food" },
      { drug: "Ondansetron", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "PO / SC", frequency: "BID", duration: "PRN 3 days" },
      { drug: "Probiotic (FortiFlora / Proviable)", doseMgPerKg: null, doseText: "1 sachet", route: "PO", frequency: "SID", duration: "10 days" },
    ],
    supportiveCare: [
      "NPO for 12h then bland diet (boiled chicken + rice), small frequent meals.",
      "Free access to water; monitor hydration & urine output.",
      "Recheck if vomiting persists >24h or haematemesis / melena develops.",
    ],
  },
  {
    id: "parvovirus",
    name: "Canine Parvovirus (CPV) Enteritis",
    species: ["canine"],
    category: "Infectious",
    clinicalSigns: "Severe vomiting, haemorrhagic diarrhoea, lethargy, marked dehydration, leukopenia.",
    diagnostics: [
      { test: "Parvo SNAP ELISA (faecal)", finding: "Positive within 3–15 days post-infection" },
      { test: "CBC", finding: "Marked leukopenia, especially lymphopenia" },
      { test: "Biochem + electrolytes + glucose", finding: "Hypoglycaemia, hypokalaemia, hypoalbuminaemia" },
      { test: "Abdominal ultrasound (optional)", finding: "Fluid-filled bowel, hyperechoic mucosa" },
    ],
    differentials: ["Haemorrhagic gastroenteritis (HGE/AHDS)", "Coronavirus enteritis", "Intussusception", "Foreign body", "Sepsis"],
    medications: [
      { drug: "Ringer's Lactate (IV fluids)", doseMgPerKg: null, doseText: "80–120 mL/kg/day", route: "IV CRI", frequency: "Continuous", duration: "3–5 days", notes: "Adjust to hydration status" },
      { drug: "Ampicillin sodium", doseMgPerKg: 22, doseText: "22 mg/kg", route: "IV", frequency: "TID", duration: "5–7 days" },
      { drug: "Metronidazole", doseMgPerKg: 10, doseText: "10 mg/kg", route: "IV slow", frequency: "BID", duration: "5 days" },
      { drug: "Maropitant citrate", doseMgPerKg: 1, doseText: "1 mg/kg", route: "IV / SC", frequency: "SID", duration: "5 days" },
      { drug: "50% Dextrose (if hypoglycaemic)", doseMgPerKg: null, doseText: "0.5 mL/kg diluted 1:4", route: "IV bolus", frequency: "PRN", duration: "As needed" },
    ],
    supportiveCare: [
      "Strict isolation; barrier nursing with dedicated PPE.",
      "Monitor blood glucose, PCV/TP, electrolytes every 12h.",
      "Nutritional support via micro-enteral feeding once vomiting controlled.",
    ],
  },
  {
    id: "pancreatitis",
    name: "Acute Pancreatitis",
    species: ["canine", "feline"],
    category: "Gastrointestinal",
    clinicalSigns: "Vomiting, abdominal pain, anorexia, prayer position (dogs), lethargy.",
    diagnostics: [
      { test: "cPL / fPL (SNAP or quantitative)", finding: "Elevated — confirms pancreatitis" },
      { test: "Abdominal ultrasound", finding: "Hyperechoic mesentery, hypoechoic pancreas, peripancreatic fluid" },
      { test: "CBC + biochem", finding: "Leukocytosis, elevated ALT/ALP, hyperbilirubinaemia" },
      { test: "Amylase / lipase (limited value)", finding: "Non-specific" },
    ],
    differentials: ["Gastroenteritis", "Foreign body", "Cholangitis (cat)", "Peritonitis", "GDV (dog)"],
    medications: [
      { drug: "IV fluids (Ringer's Lactate)", doseMgPerKg: null, doseText: "3–5 mL/kg/h then maintenance", route: "IV", frequency: "CRI", duration: "48–72h" },
      { drug: "Maropitant citrate", doseMgPerKg: 1, doseText: "1 mg/kg", route: "IV / SC", frequency: "SID", duration: "5 days" },
      { drug: "Buprenorphine", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "IV / OTM", frequency: "TID–QID", duration: "3–5 days" },
      { drug: "Metoclopramide CRI", doseMgPerKg: null, doseText: "1–2 mg/kg/day", route: "IV CRI", frequency: "Continuous", duration: "48h" },
    ],
    supportiveCare: [
      "Early enteral nutrition — low-fat diet within 24–48h.",
      "Avoid corticosteroids and lipid-rich meals.",
      "Address underlying cause (dietary indiscretion, hypertriglyceridaemia).",
    ],
  },
  {
    id: "ibd",
    name: "Inflammatory Bowel Disease (IBD)",
    species: ["canine", "feline"],
    category: "Gastrointestinal",
    clinicalSigns: "Chronic vomiting/diarrhoea >3 weeks, weight loss, poor coat, waxing-waning appetite.",
    diagnostics: [
      { test: "GI biopsy (endoscopic / surgical)", finding: "Lymphoplasmacytic / eosinophilic infiltrate — definitive" },
      { test: "Abdominal ultrasound", finding: "Thickened intestinal wall, mesenteric lymphadenopathy" },
      { test: "cTLI / fTLI, cobalamin, folate", finding: "Cobalamin often decreased" },
      { test: "Faecal exam × 3, dietary trial 6–8 weeks", finding: "Rule out parasites & food-responsive enteropathy first" },
    ],
    differentials: ["Food-responsive enteropathy", "Alimentary lymphoma", "Chronic parasitism", "EPI", "Hyperthyroidism (cat)"],
    medications: [
      { drug: "Prednisolone", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "SID initially, taper", duration: "8–12 weeks then lowest effective" },
      { drug: "Metronidazole", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID", duration: "3 weeks" },
      { drug: "Cobalamin (B12)", doseMgPerKg: null, doseText: "250 µg cat / 500–1500 µg dog", route: "SC", frequency: "Weekly x 6 then monthly", duration: "Long term if deficient" },
      { drug: "Cyclosporine (refractory)", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "SID", duration: "8–12 weeks" },
    ],
    supportiveCare: [
      "Hydrolysed or novel-protein hypoallergenic diet (Hill's z/d, Royal Canin Hypoallergenic).",
      "Monitor body weight, faecal score, CBC every 4 weeks.",
      "Avoid table scraps and non-prescription treats.",
    ],
  },
  {
    id: "megacolon",
    name: "Feline Constipation / Megacolon",
    species: ["feline"],
    category: "Gastrointestinal",
    clinicalSigns: "Straining, hard dry faeces, tenesmus, vomiting, palpable colonic distention.",
    diagnostics: [
      { test: "Abdominal radiographs", finding: "Distended colon >1.5× L5 vertebral length" },
      { test: "Rectal exam", finding: "Fecal impaction, exclude strictures / masses" },
      { test: "Biochem + electrolytes + T4", finding: "Exclude dehydration, hypercalcaemia, hypokalaemia, hyperthyroidism" },
    ],
    differentials: ["Pelvic mass / stricture", "Sacral trauma / neurogenic", "Foreign body", "Perianal hernia"],
    medications: [
      { drug: "Lactulose", doseMgPerKg: null, doseText: "0.5 mL/kg", route: "PO", frequency: "BID–TID", duration: "Titrate to soft stool" },
      { drug: "Cisapride", doseMgPerKg: null, doseText: "2.5–5 mg/cat", route: "PO", frequency: "BID–TID", duration: "Long term" },
      { drug: "Warm-water enema (± docusate sodium)", doseMgPerKg: null, doseText: "5–10 mL/kg warm", route: "PR", frequency: "PRN under sedation", duration: "As needed" },
    ],
    supportiveCare: [
      "High-fibre or moisture-rich prescription diet (Royal Canin Fibre Response).",
      "Increase water intake — fountain, wet food.",
      "Rule out and address underlying stressors; refer for subtotal colectomy if refractory.",
    ],
  },
  {
    id: "gdv",
    name: "Gastric Dilatation-Volvulus (GDV)",
    species: ["canine"],
    category: "Gastrointestinal",
    clinicalSigns: "Non-productive retching, distended abdomen, restlessness, tachycardia, shock. SURGICAL EMERGENCY.",
    diagnostics: [
      { test: "Right lateral abdominal radiograph", finding: "'Double bubble' / reverse-C sign — pathognomonic" },
      { test: "ECG", finding: "Ventricular arrhythmias in 40–50%" },
      { test: "Lactate", finding: ">6 mmol/L suggests gastric necrosis" },
      { test: "CBC + biochem + coagulation", finding: "Assess DIC, electrolyte derangements" },
    ],
    differentials: ["Simple gastric dilatation", "Splenic torsion", "Mesenteric volvulus", "Septic peritonitis"],
    medications: [
      { drug: "IV crystalloid shock bolus", doseMgPerKg: null, doseText: "20 mL/kg over 15 min, repeat up to 90 mL/kg", route: "IV", frequency: "Once", duration: "Titrate to perfusion" },
      { drug: "Trocarisation / orogastric tube", doseMgPerKg: null, doseText: "Decompress stomach", route: "Percutaneous / OG", frequency: "STAT", duration: "Pre-op" },
      { drug: "Cefazolin", doseMgPerKg: 22, doseText: "22 mg/kg", route: "IV", frequency: "q8h", duration: "24–48h peri-op" },
      { drug: "Fentanyl CRI", doseMgPerKg: null, doseText: "2–5 µg/kg/h", route: "IV CRI", frequency: "Continuous", duration: "Peri-op" },
      { drug: "Lidocaine CRI (if VTach)", doseMgPerKg: null, doseText: "25–75 µg/kg/min", route: "IV CRI", frequency: "Continuous", duration: "Until arrhythmia resolves" },
    ],
    supportiveCare: [
      "Emergency exploratory laparotomy with gastropexy — do not delay.",
      "Continuous ECG, BP, lactate monitoring 24–48h post-op.",
      "Owner education on prophylactic gastropexy for deep-chested breeds.",
    ],
  },
  {
    id: "foreign-body",
    name: "GI Foreign Body Obstruction",
    species: ["canine", "feline"],
    category: "Gastrointestinal",
    clinicalSigns: "Persistent vomiting, anorexia, abdominal pain, palpable mass, dehydration. Linear FB in cats — string under tongue.",
    diagnostics: [
      { test: "Abdominal radiographs (2 views)", finding: "Radio-opaque object or bunched intestines / plicated pattern" },
      { test: "Abdominal ultrasound", finding: "Confirms obstruction, assess for perforation / free fluid" },
      { test: "Barium series (only if no perforation)", finding: "Delayed transit or filling defect" },
      { test: "CBC + biochem + electrolytes + acid-base", finding: "Hypochloraemic metabolic alkalosis with pyloric obstruction" },
    ],
    differentials: ["Intussusception", "Neoplasia", "Pancreatitis", "Gastroenteritis", "Pyometra"],
    medications: [
      { drug: "IV fluids (Ringer's Lactate)", doseMgPerKg: null, doseText: "60–90 mL/kg/day", route: "IV", frequency: "CRI", duration: "Peri-op" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "IV / SC", frequency: "SID", duration: "3–5 days" },
      { drug: "Cefazolin", doseMgPerKg: 22, doseText: "22 mg/kg", route: "IV", frequency: "q8h", duration: "Peri-op" },
      { drug: "Methadone / Buprenorphine", doseMgPerKg: 0.2, doseText: "0.1–0.3 mg/kg (methadone)", route: "IM / IV", frequency: "QID PRN", duration: "48–72h" },
    ],
    supportiveCare: [
      "Surgical enterotomy / gastrotomy required for complete or linear obstruction.",
      "NPO 12h post-op; reintroduce small bland meals gradually.",
      "Owner counselling — pet-proofing environment.",
    ],
  },
  {
    id: "colitis",
    name: "Acute Colitis (Large-bowel Diarrhoea)",
    species: ["canine", "feline"],
    category: "Gastrointestinal",
    clinicalSigns: "Small-volume mucoid or bloody stools with tenesmus, urgency, normal appetite.",
    diagnostics: [
      { test: "Faecal exam × 3 + Giardia SNAP", finding: "Rule out parasites (whipworm, Giardia)" },
      { test: "Rectal cytology", finding: "Clostridial spores, Campylobacter-like organisms" },
      { test: "Dietary/stress history", finding: "Recent boarding, garbage, stress" },
    ],
    differentials: ["Whipworm infection", "Clostridial / Campylobacter colitis", "Dietary indiscretion", "IBD", "Colonic neoplasia"],
    medications: [
      { drug: "Metronidazole", doseMgPerKg: 15, doseText: "15 mg/kg", route: "PO", frequency: "BID", duration: "5–7 days" },
      { drug: "Sulfasalazine (dog)", doseMgPerKg: 25, doseText: "25 mg/kg", route: "PO", frequency: "TID", duration: "2–4 weeks", notes: "Monitor for KCS" },
      { drug: "Psyllium fibre (Metamucil)", doseMgPerKg: null, doseText: "1–3 tsp/meal", route: "PO", frequency: "With meals", duration: "2 weeks minimum" },
      { drug: "Probiotic", doseMgPerKg: null, doseText: "1 sachet", route: "PO", frequency: "SID", duration: "14 days" },
    ],
    supportiveCare: [
      "High-fibre or hydrolysed prescription diet.",
      "Deworm empirically with fenbendazole 50 mg/kg × 3 days if whipworm suspected.",
      "Recheck if not resolved in 7 days.",
    ],
  },
  {
    id: "hepatitis",
    name: "Hepatitis / Chronic Liver Disease",
    species: ["canine", "feline"],
    category: "Hepatic",
    clinicalSigns: "Icterus, PU/PD, inappetence, vomiting, ascites in advanced cases.",
    diagnostics: [
      { test: "Biochem panel", finding: "Elevated ALT, ALP, GGT; hypoalbuminaemia, hyperbilirubinaemia" },
      { test: "Bile acids (pre & post-prandial)", finding: "Elevated — indicates hepatic dysfunction" },
      { test: "Abdominal ultrasound + FNA / biopsy", finding: "Parenchymal changes; cytology / histopath for definitive dx" },
      { test: "Coagulation (PT/PTT)", finding: "Prolonged in end-stage disease" },
    ],
    differentials: ["Cholangitis (cat)", "Portosystemic shunt", "Hepatic neoplasia", "Copper storage disease (Bedlington/Doberman)", "Toxin exposure"],
    medications: [
      { drug: "S-Adenosylmethionine (SAMe)", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO on empty stomach", frequency: "SID", duration: "Long term" },
      { drug: "Ursodeoxycholic acid (Ursodiol)", doseMgPerKg: 15, doseText: "10–15 mg/kg", route: "PO with food", frequency: "SID", duration: "Long term" },
      { drug: "Silybin (milk thistle)", doseMgPerKg: null, doseText: "50–250 mg/dog", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Vitamin K1 (if coagulopathic)", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC", frequency: "q12h × 3 doses then SID", duration: "1 week" },
    ],
    supportiveCare: [
      "Moderate-protein, high-quality prescription liver diet.",
      "Avoid hepatotoxic drugs (NSAIDs, acetaminophen, glucocorticoids initially).",
      "Serial LFTs every 4–6 weeks; consider biopsy for targeted therapy.",
    ],
  },
  {
    id: "hepatic-lipidosis",
    name: "Feline Hepatic Lipidosis",
    species: ["feline"],
    category: "Hepatic",
    clinicalSigns: "Anorexia >3 days, weight loss, jaundice, hepatomegaly, often overweight cat with recent stress.",
    diagnostics: [
      { test: "Biochem", finding: "Markedly elevated ALP, elevated ALT, hyperbilirubinaemia" },
      { test: "Abdominal ultrasound", finding: "Diffusely hyperechoic hepatomegaly" },
      { test: "Liver FNA cytology", finding: "Marked hepatocyte vacuolation (lipid) — confirms dx" },
      { test: "Coagulation profile", finding: "Prolonged PT/PTT common" },
    ],
    differentials: ["Cholangitis", "Pancreatitis (triaditis)", "Toxic hepatopathy", "Lymphoma", "FIP"],
    medications: [
      { drug: "Placement of feeding tube (E-tube preferred)", doseMgPerKg: null, doseText: "Enteral high-protein diet", route: "E-tube", frequency: "Divided BID–QID", duration: "3–6 weeks" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC / PO", frequency: "SID", duration: "5–7 days" },
      { drug: "Vitamin K1", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC", frequency: "q12h × 3 doses", duration: "1–2 days" },
      { drug: "SAMe + Silybin", doseMgPerKg: 20, doseText: "20 mg/kg SAMe", route: "PO", frequency: "SID", duration: "6 weeks" },
      { drug: "L-Carnitine supplement", doseMgPerKg: null, doseText: "250 mg/cat", route: "PO", frequency: "SID", duration: "Duration of illness" },
    ],
    supportiveCare: [
      "Aggressive enteral nutrition is the cornerstone — do NOT starve.",
      "Correct hypokalaemia, hypophosphataemia before feeding (refeeding syndrome).",
      "Identify & treat underlying cause (pancreatitis, IBD, cholangitis).",
    ],
  },
  {
    id: "epi",
    name: "Exocrine Pancreatic Insufficiency (EPI)",
    species: ["canine"],
    category: "Gastrointestinal",
    clinicalSigns: "Chronic weight loss with polyphagia, voluminous pale malodorous stools, coprophagia. GSD predisposed.",
    diagnostics: [
      { test: "Serum cTLI", finding: "<2.5 µg/L — diagnostic" },
      { test: "Cobalamin & folate", finding: "Cobalamin usually low; folate variable" },
      { test: "Faecal exam", finding: "Rule out parasites and dysbiosis" },
    ],
    differentials: ["IBD", "Intestinal lymphoma", "SIBO / dysbiosis", "Chronic parasitism"],
    medications: [
      { drug: "Pancreatic enzyme replacement (Pancrezyme / Viokase)", doseMgPerKg: null, doseText: "1 tsp/10 kg per meal", route: "PO mixed with food", frequency: "With every meal", duration: "Life-long" },
      { drug: "Cobalamin (B12)", doseMgPerKg: null, doseText: "500–1500 µg/dog", route: "SC", frequency: "Weekly × 6 then monthly", duration: "Life-long if low" },
      { drug: "Tylosin (if concurrent dysbiosis)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID", duration: "4–6 weeks" },
    ],
    supportiveCare: [
      "Highly digestible low-fibre diet.",
      "Small frequent meals; monitor body weight weekly initially.",
      "Recheck cobalamin every 3 months.",
    ],
  },
  {
    id: "anal-sacculitis",
    name: "Anal Sac Impaction / Sacculitis",
    species: ["canine"],
    category: "Gastrointestinal",
    clinicalSigns: "Scooting, licking perineum, painful defecation, foul-smelling brown discharge, perianal swelling.",
    diagnostics: [
      { test: "Rectal exam + anal sac expression", finding: "Thick paste, purulent or bloody material" },
      { test: "Cytology of expressed material", finding: "Neutrophils, bacteria — confirms sacculitis" },
    ],
    differentials: ["Perianal fistula", "Anal sac neoplasia (adenocarcinoma)", "Perianal hernia", "Tapeworm"],
    medications: [
      { drug: "Anal sac lavage + Otibiotic instillation", doseMgPerKg: null, doseText: "Flush + fill both sacs", route: "Intra-sac", frequency: "Every 3–5 days", duration: "2–3 sessions" },
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "7–10 days" },
      { drug: "Meloxicam (short course)", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "PO", frequency: "SID", duration: "3–5 days" },
    ],
    supportiveCare: [
      "Add fibre supplement (psyllium 1 tsp/10 kg SID) to bulk stools.",
      "Weight optimisation; regular expression every 4–6 weeks if recurrent.",
      "Refer for sacculectomy if repeated abscess formation.",
    ],
  },
  {
    id: "cholangitis",
    name: "Feline Cholangitis / Cholangiohepatitis",
    species: ["feline"],
    category: "Hepatic",
    clinicalSigns: "Icterus, inappetence, vomiting, weight loss, hepatomegaly. Often triaditis (concurrent IBD + pancreatitis).",
    diagnostics: [
      { test: "Biochem", finding: "Elevated ALT, ALP, GGT, hyperbilirubinaemia" },
      { test: "Abdominal ultrasound", finding: "Thickened gallbladder wall, biliary sludge, dilated common bile duct" },
      { test: "Bile aspirate cytology + culture", finding: "Neutrophilic inflammation, E. coli / Enterococcus common" },
      { test: "Liver biopsy", finding: "Definitive — neutrophilic vs lymphocytic subtype" },
    ],
    differentials: ["Hepatic lipidosis", "Lymphoma", "FIP", "Toxic hepatopathy", "Extrahepatic biliary obstruction"],
    medications: [
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "4–6 weeks" },
      { drug: "Ursodeoxycholic acid", doseMgPerKg: 15, doseText: "10–15 mg/kg", route: "PO with food", frequency: "SID", duration: "Long term" },
      { drug: "SAMe", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Prednisolone (lymphocytic form)", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "SID, taper", duration: "8–12 weeks" },
    ],
    supportiveCare: [
      "Address concurrent pancreatitis / IBD (triaditis).",
      "Assisted feeding if inappetent >48h.",
      "Monitor LFTs every 2–4 weeks.",
    ],
  },
  {
    id: "dietary-indiscretion",
    name: "Dietary Indiscretion / Garbage Gut",
    species: ["canine"],
    category: "Gastrointestinal",
    clinicalSigns: "Acute vomiting + diarrhoea after known dietary indiscretion; usually self-limiting.",
    diagnostics: [
      { test: "History", finding: "Access to trash, table scraps, spoiled food" },
      { test: "Physical exam", finding: "Mild dehydration, unremarkable otherwise" },
      { test: "cPL SNAP (if fatty meal)", finding: "Rule out pancreatitis" },
    ],
    differentials: ["Pancreatitis", "Foreign body", "Toxin ingestion", "Parvovirus (young dog)"],
    medications: [
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "SC / PO", frequency: "SID", duration: "2–3 days" },
      { drug: "Metronidazole (if diarrhoea)", doseMgPerKg: 15, doseText: "15 mg/kg", route: "PO", frequency: "BID", duration: "3–5 days" },
      { drug: "Probiotic", doseMgPerKg: null, doseText: "1 sachet", route: "PO", frequency: "SID", duration: "7 days" },
    ],
    supportiveCare: [
      "Bland diet 3–5 days.",
      "Small frequent meals, free water.",
      "Owner education — secure garbage, no table scraps.",
    ],
  },

  // ==================== RESPIRATORY ====================
  {
    id: "kennel-cough",
    name: "Kennel Cough (Infectious Tracheobronchitis)",
    species: ["canine"],
    category: "Respiratory",
    clinicalSigns: "Dry hacking cough, retching, mild fever, recent boarding / park exposure.",
    diagnostics: [
      { test: "History + tracheal palpation cough reflex", finding: "Easily elicited paroxysmal cough" },
      { test: "Thoracic radiographs (if severe)", finding: "Usually normal; rule out pneumonia" },
      { test: "PCR panel (Bordetella, CPIV, CAV-2, Mycoplasma)", finding: "Identify pathogen if outbreak" },
    ],
    differentials: ["Bacterial pneumonia", "Collapsing trachea", "Cardiac cough (mitral disease)", "Chronic bronchitis"],
    medications: [
      { drug: "Doxycycline", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "BID", duration: "7–10 days", notes: "Follow with water" },
      { drug: "Butorphanol", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "PO", frequency: "TID PRN", duration: "5 days" },
      { drug: "Bromhexine", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "BID", duration: "7 days" },
    ],
    supportiveCare: [
      "Harness instead of collar; rest, humidified air.",
      "Avoid parks / boarding × 3 weeks.",
    ],
  },
  {
    id: "cat-uri",
    name: "Feline Upper Respiratory Infection (URI)",
    species: ["feline"],
    category: "Respiratory",
    clinicalSigns: "Sneezing, ocular/nasal discharge, conjunctivitis, inappetence, mild fever.",
    diagnostics: [
      { test: "History + physical", finding: "Serous → mucopurulent discharge, chemosis" },
      { test: "PCR panel (FHV-1, FCV, Chlamydia, Mycoplasma, Bordetella)", finding: "Identify primary pathogen — helpful in chronic cases" },
      { test: "Fluorescein stain if ocular ulcer suspected", finding: "Dendritic ulcer = FHV-1" },
    ],
    differentials: ["Cryptococcosis", "Nasal foreign body", "Neoplasia (lymphoma)", "Fungal rhinitis"],
    medications: [
      { drug: "Doxycycline", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "SID", duration: "10 days", notes: "Follow with 6 mL water" },
      { drug: "L-Lysine", doseMgPerKg: null, doseText: "250–500 mg/cat", route: "PO", frequency: "BID", duration: "14 days" },
      { drug: "Terramycin ophthalmic ointment", doseMgPerKg: null, doseText: "Thin strip", route: "OU", frequency: "TID", duration: "7 days" },
    ],
    supportiveCare: [
      "Warm humidified environment; nebulisation 10 min BID.",
      "Warm palatable food (fish/chicken).",
      "Clean discharge with saline gauze BID.",
    ],
  },
  {
    id: "feline-asthma",
    name: "Feline Asthma / Chronic Bronchial Disease",
    species: ["feline"],
    category: "Respiratory",
    clinicalSigns: "Paroxysmal cough, wheezing, expiratory dyspnoea, tachypnoea. Often 2–8 y cats.",
    diagnostics: [
      { test: "Thoracic radiographs", finding: "Bronchial / bronchointerstitial pattern, hyperinflation, right-middle lobe atelectasis" },
      { test: "BAL cytology + culture", finding: "Eosinophilic inflammation" },
      { test: "Faecal Baermann", finding: "Rule out Aelurostrongylus abstrusus" },
      { test: "Heartworm antigen + antibody", finding: "Rule out feline heartworm disease" },
    ],
    differentials: ["Feline heartworm (HARD)", "Lungworm", "Bacterial pneumonia", "Congestive heart failure", "Neoplasia"],
    medications: [
      { drug: "Prednisolone", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "SID, taper", duration: "Long term lowest effective" },
      { drug: "Fluticasone (inhaled via AeroKat)", doseMgPerKg: null, doseText: "110–220 µg/cat", route: "Inhaled", frequency: "BID", duration: "Long term" },
      { drug: "Terbutaline (acute crisis)", doseMgPerKg: null, doseText: "0.01 mg/kg", route: "SC", frequency: "Once, may repeat q4h", duration: "PRN" },
      { drug: "Salbutamol MDI (rescue)", doseMgPerKg: null, doseText: "1 puff", route: "Inhaled via spacer", frequency: "PRN", duration: "Rescue only" },
    ],
    supportiveCare: [
      "Reduce environmental triggers — dust-free litter, no smoking, unscented cleaners.",
      "Weight optimisation.",
      "Recheck rads every 6 months.",
    ],
  },
  {
    id: "pneumonia",
    name: "Bacterial Pneumonia",
    species: ["canine", "feline"],
    category: "Respiratory",
    clinicalSigns: "Productive cough, fever, tachypnoea, exercise intolerance, mucopurulent nasal discharge.",
    diagnostics: [
      { test: "Thoracic radiographs (3 views)", finding: "Alveolar pattern, cranio-ventral distribution" },
      { test: "Transtracheal / BAL culture + cytology", finding: "Neutrophils with intracellular bacteria" },
      { test: "CBC", finding: "Neutrophilic leukocytosis ± left shift" },
      { test: "Blood gas / SpO2", finding: "Hypoxaemia if severe" },
    ],
    differentials: ["Aspiration pneumonia", "Neoplasia", "Pulmonary contusion", "Fungal pneumonia", "Pulmonary oedema"],
    medications: [
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO / IV", frequency: "BID–TID", duration: "3–6 weeks" },
      { drug: "Enrofloxacin (add for severe)", doseMgPerKg: 5, doseText: "5 mg/kg (dog) / 5 mg/kg cap (cat)", route: "PO / IV", frequency: "SID", duration: "3–6 weeks", notes: "Cat max 5 mg/kg — retinal toxicity" },
      { drug: "Nebulisation with saline", doseMgPerKg: null, doseText: "Nebulise 10–15 min", route: "Inhaled", frequency: "TID–QID", duration: "Until resolution" },
    ],
    supportiveCare: [
      "Coupage after each nebulisation to loosen secretions.",
      "Oxygen supplementation if SpO2 <92%.",
      "Continue antibiotics 1–2 weeks past radiographic resolution.",
    ],
  },
  {
    id: "pulmonary-edema",
    name: "Cardiogenic Pulmonary Oedema",
    species: ["canine", "feline"],
    category: "Cardiology",
    clinicalSigns: "Tachypnoea, dyspnoea, orthopnoea, pink frothy sputum, cool extremities, murmur / gallop.",
    diagnostics: [
      { test: "Thoracic radiographs", finding: "Perihilar (dog) or diffuse (cat) alveolar pattern, cardiomegaly, distended pulmonary veins" },
      { test: "NT-proBNP / BNP", finding: "Markedly elevated — differentiates cardiac from respiratory dyspnoea" },
      { test: "Echocardiography", finding: "Underlying cardiomyopathy / valvular disease" },
      { test: "Pulse oximetry", finding: "Hypoxaemia" },
    ],
    differentials: ["Non-cardiogenic oedema (ARDS)", "Pneumonia", "Pulmonary contusion", "Pleural effusion"],
    medications: [
      { drug: "Furosemide (bolus)", doseMgPerKg: 2, doseText: "2–4 mg/kg (dog) / 1–2 mg/kg (cat)", route: "IV", frequency: "q1–2h until RR normalises", duration: "Then transition to PO" },
      { drug: "Furosemide CRI", doseMgPerKg: null, doseText: "0.5–1 mg/kg/h", route: "IV CRI", frequency: "Continuous", duration: "Until stable" },
      { drug: "Pimobendan", doseMgPerKg: 0.25, doseText: "0.25 mg/kg", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Oxygen therapy", doseMgPerKg: null, doseText: "40–60% FiO2", route: "Cage / mask", frequency: "Continuous", duration: "Until stable" },
    ],
    supportiveCare: [
      "Minimise handling stress — sedate with butorphanol 0.2 mg/kg if agitated.",
      "Sleeping RR at home <30 bpm = well controlled.",
      "Renal panel 1 week after each dose change.",
    ],
  },
  {
    id: "laryngeal-paralysis",
    name: "Laryngeal Paralysis",
    species: ["canine"],
    category: "Respiratory",
    clinicalSigns: "Inspiratory stridor, change of bark, exercise intolerance, cyanotic episodes. Older large breeds (Labs).",
    diagnostics: [
      { test: "Laryngoscopic examination under light plane anaesthesia (doxapram)", finding: "Failure of arytenoid abduction on inspiration" },
      { test: "Thoracic radiographs", finding: "Rule out aspiration pneumonia, megaoesophagus" },
      { test: "Neurologic exam", finding: "Assess for concurrent polyneuropathy (GOLPP)" },
    ],
    differentials: ["Brachycephalic airway syndrome", "Laryngeal mass", "Cervical mass", "Tracheal collapse"],
    medications: [
      { drug: "Acepromazine (acute crisis)", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "IM / IV", frequency: "Once", duration: "PRN" },
      { drug: "Butorphanol", doseMgPerKg: 0.2, doseText: "0.2 mg/kg", route: "IM", frequency: "Once", duration: "PRN sedation" },
      { drug: "Dexamethasone SP (airway oedema)", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "IV", frequency: "Once", duration: "Once" },
    ],
    supportiveCare: [
      "Definitive treatment = unilateral arytenoid lateralisation (tie-back).",
      "Cool environment, harness, avoid stress + heat.",
      "Weight reduction; feed elevated + moistened food to reduce aspiration.",
    ],
  },
  {
    id: "boas",
    name: "Brachycephalic Obstructive Airway Syndrome (BOAS)",
    species: ["canine"],
    category: "Respiratory",
    clinicalSigns: "Stertor, snoring, exercise intolerance, regurgitation, heat intolerance. Bulldogs / Pugs / Frenchies.",
    diagnostics: [
      { test: "Oral / laryngeal exam under anaesthesia", finding: "Elongated soft palate, everted saccules, stenotic nares, hypoplastic trachea" },
      { test: "Thoracic radiographs", finding: "Tracheal ratio; rule out aspiration pneumonia" },
      { test: "GI endoscopy (if regurg)", finding: "Concurrent hiatal hernia / reflux common" },
    ],
    differentials: ["Laryngeal paralysis", "Tracheal collapse", "Laryngeal / pharyngeal mass"],
    medications: [
      { drug: "Omeprazole", doseMgPerKg: 1, doseText: "1 mg/kg", route: "PO", frequency: "SID", duration: "Long term if reflux" },
      { drug: "Metoclopramide", doseMgPerKg: 0.5, doseText: "0.5 mg/kg", route: "PO", frequency: "TID (30 min before meals)", duration: "Long term" },
      { drug: "Cerenia (for regurg / vomiting)", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "SID PRN", duration: "As needed" },
    ],
    supportiveCare: [
      "Surgical correction — staphylectomy + rhinoplasty + sacculectomy is definitive.",
      "Weight optimisation; harness not collar; avoid heat / stress.",
      "Post-op monitoring for laryngeal oedema 24h.",
    ],
  },
  {
    id: "chylothorax",
    name: "Feline Chylothorax",
    species: ["feline", "canine"],
    category: "Respiratory",
    clinicalSigns: "Dyspnoea, tachypnoea, muffled heart sounds, cough. Usually gradual onset.",
    diagnostics: [
      { test: "Thoracic radiographs (post-drainage)", finding: "Pleural effusion" },
      { test: "Thoracocentesis fluid analysis", finding: "Milky fluid, triglycerides >100 mg/dL, cholesterol:trig <1" },
      { test: "Echocardiography", finding: "Rule out cardiogenic cause (HCM, DCM)" },
      { test: "Thoracic CT / lymphangiography", finding: "Identify thoracic duct anomalies" },
    ],
    differentials: ["Congestive heart failure", "Neoplasia (lymphoma, mediastinal)", "FIP", "Pyothorax"],
    medications: [
      { drug: "Rutin (benzopyrone)", doseMgPerKg: null, doseText: "50–100 mg/kg", route: "PO", frequency: "TID", duration: "Long term trial" },
      { drug: "Octreotide (refractory)", doseMgPerKg: null, doseText: "10 µg/kg", route: "SC", frequency: "TID", duration: "2–3 weeks trial" },
      { drug: "Furosemide", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "BID", duration: "Adjunct" },
    ],
    supportiveCare: [
      "Low-fat diet + medium-chain triglyceride supplementation.",
      "Serial thoracocentesis as needed for dyspnoea.",
      "Refer for thoracic duct ligation + pericardectomy if refractory >4 weeks.",
    ],
  },

  // ==================== CARDIOLOGY ====================
  {
    id: "heart-failure",
    name: "Congestive Heart Failure (CHF)",
    species: ["canine", "feline"],
    category: "Cardiology",
    clinicalSigns: "Cough, dyspnoea, exercise intolerance, syncope, ascites (dog), pleural effusion (cat).",
    diagnostics: [
      { test: "Thoracic radiographs", finding: "Cardiomegaly, pulmonary venous congestion, oedema" },
      { test: "Echocardiography", finding: "Chamber dilation, poor contractility (DCM) or LV hypertrophy (HCM)" },
      { test: "NT-proBNP", finding: "Elevated — supports cardiac dyspnoea" },
      { test: "ECG", finding: "Arrhythmias, chamber enlargement patterns" },
    ],
    differentials: ["Primary respiratory disease", "Pericardial effusion", "Non-cardiac pleural effusion", "PDA / congenital shunts"],
    medications: [
      { drug: "Furosemide", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "BID–TID", duration: "Long term, titrate down" },
      { drug: "Pimobendan", doseMgPerKg: 0.25, doseText: "0.25 mg/kg", route: "PO", frequency: "BID (1h before food)", duration: "Long term" },
      { drug: "Benazepril", doseMgPerKg: 0.25, doseText: "0.25–0.5 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Spironolactone", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
    ],
    supportiveCare: [
      "Moderate sodium restriction, no table scraps.",
      "Monitor sleeping RR at home (target <30 bpm).",
      "Rechecks echo / BNP every 3–6 months.",
    ],
  },
  {
    id: "dcm",
    name: "Dilated Cardiomyopathy (DCM)",
    species: ["canine"],
    category: "Cardiology",
    clinicalSigns: "Progressive exercise intolerance, syncope, cough, weight loss. Dobermans, Boxers, Great Danes, Cockers.",
    diagnostics: [
      { test: "Echocardiography", finding: "LV dilation, decreased fractional shortening <25%, decreased LV wall motion" },
      { test: "24h Holter ECG", finding: ">100 VPCs/24h in Dobermans = preclinical DCM" },
      { test: "NT-proBNP / cTnI", finding: "Elevated" },
      { test: "Taurine level (Cocker, Golden)", finding: "Deficiency reversible cause" },
    ],
    differentials: ["MMVD end-stage", "Pericardial effusion", "Non-cardiac dyspnoea"],
    medications: [
      { drug: "Pimobendan", doseMgPerKg: 0.25, doseText: "0.25–0.3 mg/kg", route: "PO", frequency: "BID", duration: "Life-long" },
      { drug: "Benazepril", doseMgPerKg: 0.5, doseText: "0.25–0.5 mg/kg", route: "PO", frequency: "SID", duration: "Life-long" },
      { drug: "Furosemide (with CHF)", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Sotalol (VPCs)", doseMgPerKg: 2, doseText: "1–3 mg/kg", route: "PO", frequency: "BID", duration: "Long term if arrhythmic" },
      { drug: "Taurine (Cockers)", doseMgPerKg: null, doseText: "500–1000 mg/dog", route: "PO", frequency: "BID", duration: "Life-long if deficient" },
    ],
    supportiveCare: [
      "Diet — commercial cardiac diet; avoid grain-free BEG diets.",
      "Screening echo yearly for at-risk breeds >3 y.",
      "Refer for cardiology consult; discuss ICD if syncopal VTach.",
    ],
  },
  {
    id: "hcm",
    name: "Feline Hypertrophic Cardiomyopathy (HCM)",
    species: ["feline"],
    category: "Cardiology",
    clinicalSigns: "May be silent until CHF or thromboembolism. Heart murmur, gallop, tachypnoea, hindlimb paralysis (ATE).",
    diagnostics: [
      { test: "Echocardiography", finding: "LV wall thickness >6 mm, left atrial enlargement" },
      { test: "NT-proBNP SNAP", finding: "Positive supports occult cardiomyopathy" },
      { test: "Thoracic radiographs (if dyspnoeic)", finding: "Cardiomegaly, oedema, effusion" },
      { test: "Genetic test (Maine Coon, Ragdoll MYBPC3)", finding: "Screening for at-risk breeds" },
    ],
    differentials: ["Restrictive cardiomyopathy", "Hyperthyroidism-induced hypertrophy", "Systemic hypertension", "Acromegaly"],
    medications: [
      { drug: "Clopidogrel (anti-thrombotic)", doseMgPerKg: null, doseText: "18.75 mg/cat", route: "PO", frequency: "SID", duration: "Life-long if LA enlarged" },
      { drug: "Atenolol (if outflow obstruction / tachycardia)", doseMgPerKg: 1, doseText: "1–2 mg/kg (6.25–12.5 mg/cat)", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Furosemide (CHF)", doseMgPerKg: 1, doseText: "1–2 mg/kg", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Pimobendan (advanced CHF)", doseMgPerKg: null, doseText: "1.25 mg/cat", route: "PO", frequency: "BID", duration: "Long term" },
    ],
    supportiveCare: [
      "Minimise stress — Feliway, careful handling.",
      "Owner monitors sleeping RR (<30) and hindlimb function.",
      "Serial echo every 6–12 months.",
    ],
  },
  {
    id: "mmvd",
    name: "Myxomatous Mitral Valve Disease (MMVD)",
    species: ["canine"],
    category: "Cardiology",
    clinicalSigns: "Left apical systolic murmur, cough (advanced), exercise intolerance. Small breeds — CKCS, Poodles.",
    diagnostics: [
      { test: "Auscultation + echo", finding: "Mitral regurgitation jet, LA:Ao ratio elevated" },
      { test: "Thoracic radiographs", finding: "Vertebral heart score, left atrial enlargement" },
      { test: "NT-proBNP", finding: "Elevated as stage progresses" },
    ],
    differentials: ["Chronic bronchitis / collapsing trachea (cough)", "Pulmonary hypertension", "DCM"],
    medications: [
      { drug: "Pimobendan (Stage B2 onwards)", doseMgPerKg: 0.25, doseText: "0.25 mg/kg", route: "PO", frequency: "BID", duration: "Life-long" },
      { drug: "Furosemide (Stage C)", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Benazepril", doseMgPerKg: 0.25, doseText: "0.25–0.5 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Spironolactone", doseMgPerKg: 2, doseText: "2 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
    ],
    supportiveCare: [
      "Stage B1 — monitor; Stage B2 (LA/LV enlargement) — start pimobendan.",
      "Recheck radiographs / echo every 6 months.",
      "Owner monitors RR at home; alert if >40 bpm at rest.",
    ],
  },
  {
    id: "pericardial-effusion",
    name: "Pericardial Effusion",
    species: ["canine"],
    category: "Cardiology",
    clinicalSigns: "Muffled heart sounds, weak pulses, ascites, exercise intolerance, syncope. Golden Retrievers predisposed.",
    diagnostics: [
      { test: "Echocardiography", finding: "Pericardial fluid; identify mass (haemangiosarcoma, chemodectoma)" },
      { test: "ECG", finding: "Low-voltage QRS, electrical alternans" },
      { test: "Thoracic radiographs", finding: "Globoid cardiac silhouette" },
      { test: "Fluid cytology + culture", finding: "Haemorrhagic (idiopathic vs neoplastic), septic in bacterial cases" },
    ],
    differentials: ["Right-sided CHF", "Peritoneal effusion", "Hepatic congestion", "Neoplasia primary"],
    medications: [
      { drug: "Pericardiocentesis", doseMgPerKg: null, doseText: "Ultrasound-guided drainage", route: "Percutaneous", frequency: "STAT if tamponade", duration: "Once, may recur" },
      { drug: "Furosemide (avoid — worsens perfusion)", doseMgPerKg: null, doseText: "NOT recommended pre-drainage", route: "-", frequency: "-", duration: "-" },
    ],
    supportiveCare: [
      "Refer for pericardial biopsy / subtotal pericardectomy if recurrent idiopathic.",
      "Chemotherapy referral if haemangiosarcoma confirmed (doxorubicin ± metronomic).",
      "Prognostic — idiopathic 3–5 yr; haemangiosarcoma 2–3 mo without chemo.",
    ],
  },

  // ==================== URINARY / RENAL ====================
  {
    id: "uti",
    name: "Bacterial Urinary Tract Infection (UTI)",
    species: ["canine", "feline"],
    category: "Urinary",
    clinicalSigns: "Pollakiuria, stranguria, haematuria, inappropriate urination.",
    diagnostics: [
      { test: "Urinalysis (cystocentesis sample)", finding: "Pyuria, bacteriuria, haematuria" },
      { test: "Urine culture & sensitivity", finding: "Confirms organism (E. coli most common)" },
      { test: "Abdominal radiographs / US (if recurrent)", finding: "Rule out uroliths, masses" },
    ],
    differentials: ["Uroliths", "Prostatic disease (intact male)", "Transitional cell carcinoma", "Sterile cystitis"],
    medications: [
      { drug: "Amoxicillin-Clavulanate", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "10–14 days" },
      { drug: "Meloxicam", doseMgPerKg: 0.1, doseText: "0.1 mg/kg", route: "PO", frequency: "SID", duration: "3 days", notes: "Only if hydrated & normal renal function" },
    ],
    supportiveCare: [
      "Encourage water intake — wet food, water fountain.",
      "Submit urine culture if recurrent; recheck urinalysis after therapy.",
    ],
  },
  {
    id: "flutd",
    name: "Feline Lower Urinary Tract Disease (FLUTD)",
    species: ["feline"],
    category: "Urinary",
    clinicalSigns: "Stranguria, periuria, blood in urine, vocalisation. Rule out obstruction (life-threatening).",
    diagnostics: [
      { test: "Bladder palpation + urinalysis", finding: "Small painful bladder in FIC; large firm in obstruction" },
      { test: "Abdominal radiographs + US", finding: "Rule out uroliths, masses" },
      { test: "Urine culture (if pyuria)", finding: "Only 1–3% of young cats have infection" },
    ],
    differentials: ["Urethral obstruction", "Urolithiasis (struvite, oxalate)", "Bacterial UTI (rare young cats)", "Neoplasia (older cats)"],
    medications: [
      { drug: "Buprenorphine", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "OTM / IM", frequency: "TID", duration: "3–5 days" },
      { drug: "Prazosin", doseMgPerKg: null, doseText: "0.25–0.5 mg/cat", route: "PO", frequency: "BID", duration: "10 days" },
      { drug: "Gabapentin", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID", duration: "7 days" },
    ],
    supportiveCare: [
      "Urinary prescription diet (Hill's c/d, Royal Canin Urinary S/O).",
      "Multiple litter trays (n+1), quiet environment, Feliway.",
      "Increase water intake — wet food, fountain.",
    ],
  },
  {
    id: "ckd",
    name: "Chronic Kidney Disease (CKD)",
    species: ["canine", "feline"],
    category: "Renal",
    clinicalSigns: "PU/PD, weight loss, poor coat, inappetence, halitosis, dehydration.",
    diagnostics: [
      { test: "Serum BUN + creatinine + SDMA + phosphorus", finding: "Progressive azotaemia; SDMA elevates earlier" },
      { test: "Urinalysis (USG)", finding: "Isosthenuria (USG 1.008–1.012)" },
      { test: "UPC ratio", finding: ">0.4 (dog) / >0.4 (cat) = proteinuric" },
      { test: "Blood pressure", finding: "Systemic hypertension in 20–65% of CKD cats" },
      { test: "IRIS staging + abdominal US", finding: "Chronic changes, no obstructive cause" },
    ],
    differentials: ["Acute kidney injury", "Pyelonephritis", "Hypercalcaemia", "Renal lymphoma", "Ureteral obstruction"],
    medications: [
      { drug: "Benazepril", doseMgPerKg: 0.25, doseText: "0.25–0.5 mg/kg", route: "PO", frequency: "SID", duration: "Long term if proteinuric" },
      { drug: "Aluminium hydroxide (phosphate binder)", doseMgPerKg: 30, doseText: "30–90 mg/kg", route: "PO with food", frequency: "Divided with meals", duration: "Long term" },
      { drug: "SC fluids (Ringer's Lactate)", doseMgPerKg: null, doseText: "75–150 mL/cat or 20 mL/kg", route: "SC", frequency: "q1–3 days", duration: "As required" },
      { drug: "Mirtazapine (appetite)", doseMgPerKg: null, doseText: "1.88 mg/cat", route: "PO", frequency: "q48h", duration: "PRN" },
      { drug: "Amlodipine (hypertension)", doseMgPerKg: null, doseText: "0.625–1.25 mg/cat", route: "PO", frequency: "SID", duration: "Long term if BP >160 mmHg" },
    ],
    supportiveCare: [
      "Renal prescription diet (low P, moderate high-quality protein).",
      "Rechecks every 3 months — BUN, creatinine, SDMA, P, UPC, BP.",
      "Owner education — encourage water, monitor appetite.",
    ],
  },
  {
    id: "aki",
    name: "Acute Kidney Injury (AKI)",
    species: ["canine", "feline"],
    category: "Renal",
    clinicalSigns: "Acute onset anorexia, vomiting, oliguria/anuria or polyuria, dehydration. History of toxin exposure or infection.",
    diagnostics: [
      { test: "Serum creatinine + SDMA + BUN + electrolytes", finding: "Rapid rise in creatinine; hyperkalaemia if oliguric" },
      { test: "Urinalysis", finding: "Isosthenuria, glucosuria, casts (renal tubular)" },
      { test: "Abdominal US", finding: "Enlarged hyperechoic kidneys, perinephric fluid" },
      { test: "Toxin history / drug review", finding: "Ethylene glycol, lily (cat), NSAID, aminoglycoside, grapes/raisins (dog)" },
      { test: "Leptospirosis MAT (dog)", finding: "Rising titre; PCR blood/urine" },
    ],
    differentials: ["Chronic kidney disease", "Pre-renal (dehydration, shock)", "Post-renal (obstruction)", "Leptospirosis", "Toxin exposure"],
    medications: [
      { drug: "IV fluids (LRS)", doseMgPerKg: null, doseText: "Correct dehydration then maintenance + ongoing losses", route: "IV", frequency: "CRI", duration: "Until stable" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "IV / SC", frequency: "SID", duration: "5–7 days" },
      { drug: "Furosemide (only if oliguric + hydrated)", doseMgPerKg: 2, doseText: "2 mg/kg", route: "IV", frequency: "PRN", duration: "Trial" },
      { drug: "Ampicillin + Enrofloxacin (Leptospira empiric)", doseMgPerKg: 22, doseText: "Ampi 22 mg/kg + Enro 5 mg/kg", route: "IV", frequency: "TID / SID", duration: "2 weeks Doxy follow-up" },
    ],
    supportiveCare: [
      "Refer for haemodialysis if anuric >24h despite fluids.",
      "Monitor urine output, weight, K+, phosphate q6h.",
      "Owner PPE if Leptospirosis suspected — zoonotic.",
    ],
  },
  {
    id: "struvite-stones",
    name: "Struvite Urolithiasis",
    species: ["canine", "feline"],
    category: "Urinary",
    clinicalSigns: "Haematuria, stranguria, pollakiuria; palpable bladder stones on gentle palpation.",
    diagnostics: [
      { test: "Abdominal radiographs", finding: "Radio-opaque uroliths" },
      { test: "Urinalysis", finding: "Alkaline pH, struvite crystalluria, pyuria (dogs — infection-induced)" },
      { test: "Urine culture", finding: "Staphylococcus / Proteus urease-producers (dogs)" },
      { test: "Stone analysis (post-removal)", finding: "Magnesium ammonium phosphate crystals" },
    ],
    differentials: ["Calcium oxalate uroliths", "Urate / cystine stones", "TCC", "Idiopathic FLUTD (cat)"],
    medications: [
      { drug: "Amoxicillin-Clavulanate (dog — treat infection)", doseMgPerKg: 12.5, doseText: "12.5 mg/kg", route: "PO", frequency: "BID", duration: "4–6 weeks or until dissolved" },
      { drug: "Prescription dissolution diet (Hill's s/d, Royal Canin Urinary S/O)", doseMgPerKg: null, doseText: "As sole diet", route: "PO", frequency: "All meals", duration: "4–12 weeks" },
    ],
    supportiveCare: [
      "Increase water intake — wet food, fountain.",
      "Recheck radiographs monthly to assess dissolution.",
      "Surgical / laser lithotripsy if urethral obstruction imminent.",
    ],
  },
  {
    id: "blocked-cat",
    name: "Feline Urethral Obstruction ('Blocked Cat')",
    species: ["feline"],
    category: "Urinary",
    clinicalSigns: "Non-productive straining, vocalisation, large firm painful bladder, lethargy → collapse. LIFE-THREATENING.",
    diagnostics: [
      { test: "Bladder palpation + physical exam", finding: "Large firm bladder = obstruction" },
      { test: "K+, creatinine, BUN, pH, iCa", finding: "Hyperkalaemia, azotaemia, metabolic acidosis" },
      { test: "ECG", finding: "Bradycardia, tented T waves, absent P waves with severe K+" },
      { test: "Abdominal radiographs post-cath", finding: "Radio-opaque uroliths / plug" },
    ],
    differentials: ["Urolithiasis", "Urethral plug", "Neoplasia (older cat)", "Urethral stricture"],
    medications: [
      { drug: "IV fluids (0.9% NaCl or LRS)", doseMgPerKg: null, doseText: "Aggressive rehydration, then maintenance", route: "IV", frequency: "CRI", duration: "48–72h" },
      { drug: "Calcium gluconate 10% (hyperkalaemia)", doseMgPerKg: null, doseText: "0.5–1 mL/kg", route: "IV slow over 5 min", frequency: "Once", duration: "PRN cardiac protection" },
      { drug: "Regular insulin + dextrose (K+ shift)", doseMgPerKg: null, doseText: "0.25 U/kg + 2 g dextrose / U", route: "IV", frequency: "Once", duration: "Monitor glucose q1h" },
      { drug: "Prazosin", doseMgPerKg: null, doseText: "0.25–0.5 mg/cat", route: "PO", frequency: "BID", duration: "10 days post-unblock" },
      { drug: "Buprenorphine", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "OTM", frequency: "TID", duration: "3–5 days" },
    ],
    supportiveCare: [
      "Sedated urethral catheterisation, retropulse & flush; indwelling U-cath 24–72h.",
      "Prescription urinary diet + Feliway + multi-cat litter tray setup.",
      "Owner education — re-block rate 15–30%; consider perineal urethrostomy if recurrent.",
    ],
  },
  {
    id: "pyelonephritis",
    name: "Pyelonephritis (Ascending Kidney Infection)",
    species: ["canine", "feline"],
    category: "Renal",
    clinicalSigns: "Fever, lumbar pain, PU/PD, vomiting, lethargy. Often complicating lower UTI.",
    diagnostics: [
      { test: "Urinalysis + culture (pyelocentesis or cystocentesis)", finding: "Pyuria, bacteriuria, WBC casts" },
      { test: "CBC + biochem + SDMA", finding: "Neutrophilia, mild-moderate azotaemia" },
      { test: "Abdominal US", finding: "Dilated renal pelvis, hyperechoic renal cortex" },
    ],
    differentials: ["Lower UTI", "Pyometra (intact female)", "Renal lymphoma", "Leptospirosis"],
    medications: [
      { drug: "Enrofloxacin (empirical while awaiting culture)", doseMgPerKg: 10, doseText: "10 mg/kg (dog) / 5 mg/kg (cat max)", route: "PO / SC", frequency: "SID", duration: "4–6 weeks" },
      { drug: "Amoxicillin-Clavulanate (alternative)", doseMgPerKg: 20, doseText: "20 mg/kg", route: "PO", frequency: "BID", duration: "4–6 weeks" },
      { drug: "IV fluids", doseMgPerKg: null, doseText: "60–90 mL/kg/day", route: "IV", frequency: "CRI", duration: "48h if azotaemic" },
    ],
    supportiveCare: [
      "Recheck urine culture 1 week after starting therapy AND 1 week post-completion.",
      "Address predisposing factors — uroliths, hyperadrenocorticism, diabetes.",
    ],
  },

  // ==================== ENDOCRINE ====================
  {
    id: "hyperthyroidism",
    name: "Feline Hyperthyroidism",
    species: ["feline"],
    category: "Endocrine",
    clinicalSigns: "Weight loss with polyphagia, tachycardia, hyperactivity, palpable thyroid nodule, PU/PD.",
    diagnostics: [
      { test: "Serum total T4", finding: ">4.0 µg/dL (>50 nmol/L) — confirms" },
      { test: "Biochem + urinalysis", finding: "Elevated ALT/ALP common; check baseline renal function" },
      { test: "T3 suppression / free T4 (if borderline)", finding: "Free T4 elevated confirms mild cases" },
      { test: "Blood pressure", finding: "Systemic hypertension in ~25%" },
    ],
    differentials: ["Diabetes mellitus", "Chronic kidney disease", "GI lymphoma / IBD", "Cardiac disease (secondary HCM)"],
    medications: [
      { drug: "Methimazole", doseMgPerKg: null, doseText: "2.5 mg/cat", route: "PO", frequency: "BID", duration: "Recheck T4 in 2–3 weeks, titrate" },
      { drug: "Methimazole transdermal (PLO gel)", doseMgPerKg: null, doseText: "2.5 mg per ear", route: "Transdermal", frequency: "BID", duration: "Long term option" },
      { drug: "Atenolol (tachycardia)", doseMgPerKg: null, doseText: "6.25–12.5 mg/cat", route: "PO", frequency: "SID–BID", duration: "Until euthyroid" },
    ],
    supportiveCare: [
      "Recheck T4, biochem, CBC every 2–3 weeks until stable.",
      "Discuss radioactive iodine (I-131) — curative.",
      "Monitor renal parameters — hyperthyroidism can mask CKD.",
    ],
  },
  {
    id: "diabetes",
    name: "Diabetes Mellitus",
    species: ["canine", "feline"],
    category: "Endocrine",
    clinicalSigns: "PU/PD, polyphagia, weight loss, cataracts (dog), plantigrade stance (cat).",
    diagnostics: [
      { test: "Fasting blood glucose", finding: ">10 mmol/L (>180 mg/dL) consistent; cats affected by stress" },
      { test: "Urinalysis", finding: "Glucosuria ± ketonuria" },
      { test: "Serum fructosamine", finding: "Elevated — reflects sustained hyperglycaemia" },
      { test: "CBC + biochem + T4 (cat) + urine culture", finding: "Rule out concurrent UTI, HAC, hyperthyroidism, pancreatitis" },
    ],
    differentials: ["Stress hyperglycaemia (cat)", "Hyperadrenocorticism", "Diestrus / progestagens", "Acromegaly (cat)"],
    medications: [
      { drug: "Caninsulin / Vetsulin (dog & cat)", doseMgPerKg: null, doseText: "Start 0.25–0.5 U/kg", route: "SC", frequency: "BID with meals", duration: "Long term — titrate on curve" },
      { drug: "Insulin glargine (cat, preferred)", doseMgPerKg: null, doseText: "0.25 U/kg or 1–2 U/cat start", route: "SC", frequency: "BID", duration: "Long term" },
    ],
    supportiveCare: [
      "Consistent diet & feeding times; high-protein low-carb (cat), moderate-fibre (dog).",
      "Weekly home glucose curves initially; fructosamine every 3 months.",
      "Owner education on hypoglycaemia — keep karo syrup at home.",
    ],
  },
  {
    id: "hypothyroidism",
    name: "Canine Hypothyroidism",
    species: ["canine"],
    category: "Endocrine",
    clinicalSigns: "Lethargy, weight gain, cold intolerance, symmetrical alopecia (tail = 'rat tail'), pyoderma, myxoedema.",
    diagnostics: [
      { test: "Total T4 + Free T4 (equilibrium dialysis) + TSH", finding: "Low T4, low free T4, elevated TSH" },
      { test: "CBC + biochem", finding: "Non-regenerative anaemia, hypercholesterolaemia, elevated CK" },
      { test: "Rule out euthyroid sick syndrome", finding: "Non-thyroidal illness or drugs (glucocorticoids, phenobarb) confound T4" },
    ],
    differentials: ["Cushing's disease", "Sex-hormone dermatosis", "Nutritional dermatosis", "Non-thyroidal illness"],
    medications: [
      { drug: "Levothyroxine", doseMgPerKg: 0.02, doseText: "0.02 mg/kg", route: "PO", frequency: "SID–BID", duration: "Life-long, recheck 4–6 weeks" },
    ],
    supportiveCare: [
      "Recheck total T4 6 h post-pill after 4–6 weeks; target 25–48 nmol/L.",
      "Improvement in activity 1–2 weeks; skin changes 4–8 weeks.",
      "Lifelong therapy — recheck annually.",
    ],
  },
  {
    id: "cushings",
    name: "Hyperadrenocorticism (Cushing's Disease)",
    species: ["canine"],
    category: "Endocrine",
    clinicalSigns: "PU/PD, polyphagia, pot-belly, muscle wasting, alopecia, pyoderma, hepatomegaly, panting.",
    diagnostics: [
      { test: "ACTH stimulation test", finding: "Post-ACTH cortisol >600 nmol/L confirms HAC" },
      { test: "Low-dose dexamethasone suppression (LDDS)", finding: "Failure to suppress cortisol at 8h — confirms & helps localise" },
      { test: "Abdominal US", finding: "Bilateral adrenomegaly (PDH) or unilateral mass (adrenal tumour)" },
      { test: "Urine cortisol:creatinine ratio (screening)", finding: "Elevated but non-specific — good rule-out" },
    ],
    differentials: ["Diabetes mellitus", "Chronic kidney disease", "Iatrogenic HAC (exogenous steroids)", "Pyometra"],
    medications: [
      { drug: "Trilostane", doseMgPerKg: 2, doseText: "1–3 mg/kg", route: "PO with food", frequency: "SID–BID", duration: "Long term; recheck ACTH stim 10–14 days" },
      { drug: "Mitotane (older alternative)", doseMgPerKg: 25, doseText: "25 mg/kg", route: "PO with food", frequency: "SID induction, then weekly", duration: "Long term maintenance" },
    ],
    supportiveCare: [
      "Recheck ACTH stim / clinical signs / biochem every 3 months.",
      "Warn owners about Addisonian crisis signs (weakness, vomiting, collapse).",
      "Surgical adrenalectomy if unilateral adrenal tumour.",
    ],
  },
  {
    id: "addisons",
    name: "Hypoadrenocorticism (Addison's Disease)",
    species: ["canine"],
    category: "Endocrine",
    clinicalSigns: "Waxing-waning lethargy, vomiting, diarrhoea, weakness, collapse. Classic — young female dog in shock with bradycardia.",
    diagnostics: [
      { test: "ACTH stimulation test", finding: "Pre- and post-cortisol both <55 nmol/L — DIAGNOSTIC" },
      { test: "Na:K ratio", finding: "<27:1 supports typical Addison's" },
      { test: "Biochem", finding: "Hyperkalaemia, hyponatraemia, hypoglycaemia, azotaemia" },
      { test: "ECG", finding: "Bradycardia, tented T waves with K+ >7 mmol/L" },
    ],
    differentials: ["Acute renal failure", "Whipworm infestation", "Sepsis", "GI foreign body", "Insulinoma"],
    medications: [
      { drug: "IV fluids 0.9% NaCl (crisis)", doseMgPerKg: null, doseText: "60–90 mL/kg initial bolus, then maintenance", route: "IV", frequency: "CRI", duration: "Until stable" },
      { drug: "Dexamethasone SP (crisis)", doseMgPerKg: 0.1, doseText: "0.1–0.2 mg/kg", route: "IV", frequency: "Once, repeat q12h", duration: "Until oral tolerated" },
      { drug: "DOCP (Zycortal / Percorten)", doseMgPerKg: 2.2, doseText: "2.2 mg/kg", route: "SC / IM", frequency: "Every 25–30 days", duration: "Life-long, monitor electrolytes" },
      { drug: "Prednisolone (maintenance)", doseMgPerKg: 0.1, doseText: "0.1–0.2 mg/kg", route: "PO", frequency: "SID", duration: "Life-long; increase 2–5× at stress" },
    ],
    supportiveCare: [
      "Electrolyte monitoring — every 25–30 days initially, then quarterly.",
      "Owner education — increase pred at times of stress (boarding, surgery, illness).",
      "Excellent prognosis with lifelong therapy.",
    ],
  },
  {
    id: "dka",
    name: "Diabetic Ketoacidosis (DKA)",
    species: ["canine", "feline"],
    category: "Endocrine",
    clinicalSigns: "Anorexia, vomiting, lethargy, dehydration, tachypnoea (Kussmaul), sweet-smelling breath, PU/PD.",
    diagnostics: [
      { test: "Blood glucose + urine ketones + β-hydroxybutyrate", finding: "Hyperglycaemia >14 mmol/L, ketonaemia/ketonuria" },
      { test: "Venous blood gas", finding: "Metabolic acidosis (pH <7.3, HCO3 <15)" },
      { test: "Electrolytes", finding: "Hypokalaemia, hypophosphataemia, hyponatraemia" },
      { test: "cPL/fPL, urine culture, T4 (cat)", finding: "Identify precipitating illness (pancreatitis, UTI, hyperthyroid)" },
    ],
    differentials: ["Uncomplicated DM", "Pancreatitis (concurrent common)", "Sepsis", "Renal failure"],
    medications: [
      { drug: "IV fluids (0.9% NaCl → LRS)", doseMgPerKg: null, doseText: "Correct dehydration over 12–24h then maintenance", route: "IV", frequency: "CRI", duration: "24–72h" },
      { drug: "Regular (short-acting) insulin CRI", doseMgPerKg: null, doseText: "2.2 U/kg/day in 250 mL 0.9% NaCl", route: "IV CRI", frequency: "Titrated to glucose", duration: "Until ketones clear" },
      { drug: "KCl supplementation", doseMgPerKg: null, doseText: "20–40 mEq/L based on serum K+", route: "IV", frequency: "Add to fluids", duration: "Monitor q6h" },
      { drug: "Maropitant", doseMgPerKg: 1, doseText: "1 mg/kg", route: "IV / SC", frequency: "SID", duration: "5 days" },
    ],
    supportiveCare: [
      "Monitor glucose q1–2h; ketones q6h; electrolytes q6h.",
      "Transition to subcutaneous long-acting insulin only after eating & no vomiting.",
      "Identify and treat underlying illness.",
    ],
  },
  {
    id: "insulinoma",
    name: "Insulinoma (Canine)",
    species: ["canine"],
    category: "Endocrine",
    clinicalSigns: "Episodic weakness, collapse, seizures — improve with feeding. Older large-breed dogs.",
    diagnostics: [
      { test: "Paired fasting glucose + serum insulin", finding: "Hypoglycaemia (<3.3 mmol/L) with inappropriately normal / elevated insulin" },
      { test: "Abdominal US ± CT", finding: "Pancreatic mass ± hepatic mets" },
      { test: "Amended insulin:glucose ratio", finding: "Elevated" },
    ],
    differentials: ["Addison's disease", "Hepatic failure", "Sepsis", "Xylitol toxicity", "Iatrogenic insulin overdose"],
    medications: [
      { drug: "Prednisolone", doseMgPerKg: 0.5, doseText: "0.5–1 mg/kg", route: "PO", frequency: "SID", duration: "Long term, titrate to signs" },
      { drug: "Diazoxide (refractory)", doseMgPerKg: 10, doseText: "10 mg/kg", route: "PO", frequency: "BID", duration: "Long term" },
      { drug: "Dextrose 50% (acute hypoglycaemia)", doseMgPerKg: null, doseText: "0.5–1 mL/kg diluted 1:4", route: "IV bolus", frequency: "PRN", duration: "PRN" },
    ],
    supportiveCare: [
      "Small frequent meals (q4–6h) rich in complex carbs + protein.",
      "Surgical partial pancreatectomy is treatment of choice.",
      "Owner keeps oral glucose gel at home for emergencies.",
    ],
  },

  // ==================== DERMATOLOGY (part 1 — remaining in file B) ====================
  {
    id: "otitis-externa",
    name: "Otitis Externa (Bacterial / Yeast)",
    species: ["canine", "feline"],
    category: "Dermatology",
    clinicalSigns: "Head shaking, ear scratching, malodour, erythema, aural discharge.",
    diagnostics: [
      { test: "Otoscopic exam", finding: "Erythema, ceruminous or purulent discharge, TM integrity" },
      { test: "Ear cytology", finding: "Yeast (Malassezia), cocci (Staph), rods (Pseudomonas)" },
      { test: "Culture + sensitivity (if rods / chronic)", finding: "Guides antibiotic choice — often Pseudomonas" },
    ],
    differentials: ["Otitis media/interna", "Ear polyp (cat)", "Foreign body (grass awn)", "Neoplasia", "Ear mites"],
    medications: [
      { drug: "Epi-Otic ear cleanser", doseMgPerKg: null, doseText: "Flush affected ear", route: "Topical", frequency: "SID", duration: "10 days" },
      { drug: "Otibiotic / Mometamax otic suspension", doseMgPerKg: null, doseText: "4–8 drops", route: "Topical AU/AS", frequency: "BID", duration: "7–10 days" },
      { drug: "Cephalexin (if severe / TM ruptured — oral)", doseMgPerKg: 22, doseText: "22 mg/kg", route: "PO", frequency: "BID", duration: "10 days" },
    ],
    supportiveCare: [
      "Clean ears before each topical application; dry thoroughly.",
      "Recheck otoscopy at day 10–14.",
      "Investigate underlying allergy if recurrent.",
    ],
  },
  {
    id: "atopic-dermatitis",
    name: "Atopic Dermatitis / Allergic Skin Disease",
    species: ["canine", "feline"],
    category: "Dermatology",
    clinicalSigns: "Pruritus, erythema, alopecia, secondary pyoderma, seasonal or perennial.",
    diagnostics: [
      { test: "Diagnosis of exclusion + Favrot criteria", finding: "≥5 Favrot criteria = 80% likelihood of AD" },
      { test: "Flea comb + prevention trial", finding: "Rule out FAD first" },
      { test: "Skin scrape + cytology", finding: "Rule out mange + secondary infections" },
      { test: "Diet elimination trial (8 weeks)", finding: "Rule out food-responsive dermatosis" },
      { test: "Intradermal / serum IgE allergy testing", finding: "Identify allergens for immunotherapy" },
    ],
    differentials: ["Food allergy", "Flea allergy dermatitis", "Sarcoptic mange", "Cheyletiellosis", "Bacterial pyoderma"],
    medications: [
      { drug: "Oclacitinib (Apoquel)", doseMgPerKg: 0.5, doseText: "0.4–0.6 mg/kg", route: "PO", frequency: "BID × 14 d → SID", duration: "As required" },
      { drug: "Cytopoint (lokivetmab)", doseMgPerKg: 2, doseText: "2 mg/kg", route: "SC", frequency: "Every 4–8 weeks", duration: "Long term" },
      { drug: "Cetirizine", doseMgPerKg: 1, doseText: "1 mg/kg", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Omega-3 EPA/DHA", doseMgPerKg: null, doseText: "50 mg/kg EPA", route: "PO", frequency: "SID", duration: "Long term" },
      { drug: "Chlorhexidine 2% shampoo", doseMgPerKg: null, doseText: "Bathe, 10 min contact", route: "Topical", frequency: "2×/week", duration: "4 weeks" },
    ],
    supportiveCare: [
      "Strict year-round flea prevention.",
      "Diet trial 8 weeks if food allergy suspected.",
      "Environmental — HEPA filter, weekly bedding wash at 60 °C.",
    ],
  },
  {
    id: "pyoderma",
    name: "Superficial Bacterial Pyoderma",
    species: ["canine", "feline"],
    category: "Dermatology",
    clinicalSigns: "Papules, pustules, epidermal collarettes, crusting, pruritus.",
    diagnostics: [
      { test: "Skin cytology (impression / tape prep)", finding: "Cocci + neutrophils = Staphylococcus" },
      { test: "Bacterial culture + sensitivity", finding: "MRSP surveillance; guide antibiotic if chronic" },
      { test: "Search for underlying cause", finding: "Allergy, endocrinopathy, ectoparasites" },
    ],
    differentials: ["Demodicosis", "Dermatophytosis", "Pemphigus foliaceus", "Malassezia dermatitis"],
    medications: [
      { drug: "Cephalexin", doseMgPerKg: 22, doseText: "22 mg/kg", route: "PO", frequency: "BID", duration: "21 days minimum" },
      { drug: "Chlorhexidine 4% shampoo", doseMgPerKg: null, doseText: "Whole body, 10 min contact", route: "Topical", frequency: "2–3×/week", duration: "3 weeks" },
    ],
    supportiveCare: [
      "Continue antibiotics 7 days past clinical resolution.",
      "Investigate underlying allergy / endocrinopathy / ectoparasites.",
    ],
  },
  {
    id: "ringworm",
    name: "Dermatophytosis (Ringworm)",
    species: ["canine", "feline"],
    category: "Dermatology",
    clinicalSigns: "Circular alopecic lesions, scaling, mild pruritus. ZOONOTIC — gloves advised.",
    diagnostics: [
      { test: "Wood's lamp exam", finding: "Apple-green fluorescence in ~50% of M. canis" },
      { test: "Trichogram (KOH mount)", finding: "Hyphae or arthrospores on hair shaft" },
      { test: "Fungal culture (DTM)", finding: "Colour change with white colony — GOLD STANDARD" },
      { test: "PCR", finding: "Faster confirmatory test" },
    ],
    differentials: ["Demodicosis", "Bacterial folliculitis", "Pemphigus foliaceus", "Alopecia X"],
    medications: [
      { drug: "Itraconazole", doseMgPerKg: 5, doseText: "5 mg/kg", route: "PO", frequency: "SID pulse (week-on/week-off)", duration: "3 pulses" },
      { drug: "Miconazole + Chlorhexidine shampoo", doseMgPerKg: null, doseText: "Whole body, 10 min contact", route: "Topical", frequency: "2×/week", duration: "6 weeks" },
      { drug: "Lime sulfur dip 2%", doseMgPerKg: null, doseText: "Full dip", route: "Topical", frequency: "Weekly", duration: "6 weeks" },
    ],
    supportiveCare: [
      "Clip long hair; discard bedding weekly.",
      "Environmental — vacuum daily, 1:10 bleach on hard surfaces.",
      "Continue until 2 negative fungal cultures 2 weeks apart.",
    ],
  },
  {
    id: "flea-allergy",
    name: "Flea Allergy Dermatitis (FAD)",
    species: ["canine", "feline"],
    category: "Dermatology",
    clinicalSigns: "Intense pruritus of dorsal lumbosacral region (dog) or 'miliary' papulocrustous dermatitis (cat).",
    diagnostics: [
      { test: "Flea combing", finding: "Fleas or flea dirt (positive on wet white paper)" },
      { test: "Response to strict flea control trial", finding: "Signs resolve within 4–6 weeks" },
      { test: "Skin cytology", finding: "Rule out secondary bacterial / yeast infection" },
    ],
    differentials: ["Atopic dermatitis", "Food allergy", "Sarcoptic mange", "Bacterial pyoderma"],
    medications: [
      { drug: "Fluralaner (Bravecto) or Afoxolaner (NexGard)", doseMgPerKg: null, doseText: "Weight-band chew / topical", route: "PO / Topical", frequency: "Every 12 weeks (Bravecto) / Monthly", duration: "Life-long" },
      { drug: "Oclacitinib (Apoquel)", doseMgPerKg: 0.5, doseText: "0.4–0.6 mg/kg", route: "PO", frequency: "BID × 14 d → SID", duration: "Until controlled" },
      { drug: "Chlorhexidine + Miconazole shampoo", doseMgPerKg: null, doseText: "Whole body 10 min", route: "Topical", frequency: "2×/week", duration: "2 weeks" },
    ],
    supportiveCare: [
      "Treat ALL in-contact pets; environmental IGR (methoprene) for indoor infestations.",
      "Educate owner — see one flea = 100 in environment.",
    ],
  },
  {
    id: "malassezia",
    name: "Malassezia Dermatitis",
    species: ["canine", "feline"],
    category: "Dermatology",
    clinicalSigns: "Greasy, malodorous skin, erythema, alopecia — especially ventral neck, axillae, interdigital, ears.",
    diagnostics: [
      { test: "Skin cytology (tape prep)", finding: "'Peanut-shaped' budding yeast (>4/hpf)" },
      { test: "Rule out underlying disease", finding: "Allergy, endocrinopathy, cutaneous adverse food reaction" },
    ],
    differentials: ["Bacterial pyoderma", "Atopic dermatitis", "Dermatophytosis", "Endocrine skin disease"],
    medications: [
      { drug: "Ketoconazole", doseMgPerKg: 10, doseText: "5–10 mg/kg", route: "PO", frequency: "SID", duration: "3–4 weeks" },
      { drug: "Miconazole + Chlorhexidine shampoo (Malaseb)", doseMgPerKg: null, doseText: "10 min contact", route: "Topical", frequency: "2–3×/week", duration: "3 weeks" },
      { drug: "Miconazole ear wipes (localised)", doseMgPerKg: null, doseText: "Daily wipe", route: "Topical", frequency: "SID", duration: "Until resolved" },
    ],
    supportiveCare: [
      "Treat underlying allergy / endocrinopathy — high recurrence otherwise.",
      "Monitor LFTs if long-term azole therapy.",
    ],
  },
  {
    id: "sarcoptic-mange",
    name: "Sarcoptic Mange (Scabies)",
    species: ["canine"],
    category: "Parasitic",
    clinicalSigns: "Intense pruritus, ventral / ear-margin alopecia, crusting, positive pinnal-pedal reflex. ZOONOTIC.",
    diagnostics: [
      { test: "Deep skin scrape (multiple sites, ear margins)", finding: "Sarcoptes scabiei — low sensitivity, high specificity" },
      { test: "Pinnal-pedal scratch reflex", finding: "Positive in 80% — supports diagnosis" },
      { test: "Serology (IgG antibody)", finding: "Positive after 3–5 weeks infection" },
      { test: "Therapeutic trial", finding: "Response confirms diagnosis if scrape negative" },
    ],
    differentials: ["Atopic dermatitis", "Food allergy", "Bacterial pyoderma", "Cheyletiellosis"],
    medications: [
      { drug: "Afoxolaner (NexGard)", doseMgPerKg: null, doseText: "Weight-band chew", route: "PO", frequency: "Monthly", duration: "2 doses" },
      { drug: "Selamectin spot-on", doseMgPerKg: 6, doseText: "6 mg/kg", route: "Topical", frequency: "Every 14 days", duration: "3 doses (alternative)" },
    ],
    supportiveCare: [
      "Advise gloves for handlers; treat contact animals.",
      "Colloidal oatmeal shampoo for antipruritic effect.",
    ],
  },
  {
    id: "demodicosis",
    name: "Demodicosis (Demodex canis)",
    species: ["canine"],
    category: "Parasitic",
    clinicalSigns: "Focal or generalised alopecia, comedones, secondary pyoderma. Juvenile vs adult-onset.",
    diagnostics: [
      { test: "Deep skin scrape (multiple sites)", finding: ">1 mite / scrape confirms; count mites for monitoring" },
      { test: "Trichogram (hair pluck)", finding: "Useful in periocular/paw areas where scraping difficult" },
      { test: "Investigate immunosuppression in adult-onset", finding: "HAC, hypothyroidism, neoplasia, iatrogenic steroids" },
    ],
    differentials: ["Dermatophytosis", "Superficial pyoderma", "Deep pyoderma", "Sebaceous adenitis"],
    medications: [
      { drug: "Fluralaner (Bravecto)", doseMgPerKg: null, doseText: "Weight-band chew", route: "PO", frequency: "Every 12 weeks", duration: "Until 2 negative scrapes 4 weeks apart" },
      { drug: "Cephalexin (secondary pyoderma)", doseMgPerKg: 22, doseText: "22 mg/kg", route: "PO", frequency: "BID", duration: "21–28 days" },
    ],
    supportiveCare: [
      "Monthly skin scrapes; monitor mite count.",
      "Search for underlying immunosuppression in adult-onset cases.",
    ],
  },
  {
    id: "ear-mites",
    name: "Otoacariasis (Ear Mites — Otodectes cynotis)",
    species: ["canine", "feline"],
    category: "Parasitic",
    clinicalSigns: "Intense head shaking, dark coffee-ground discharge, aural pruritus.",
    diagnostics: [
      { test: "Otoscopic exam", finding: "White mites moving in dark cerumen" },
      { test: "Ear swab cytology (mineral oil)", finding: "Otodectes mites, eggs" },
    ],
    differentials: ["Bacterial otitis", "Yeast otitis", "Ear polyp (cat)", "Sarcoptic mange"],
    medications: [
      { drug: "Selamectin (Revolution) spot-on", doseMgPerKg: 6, doseText: "6 mg/kg", route: "Topical", frequency: "Monthly", duration: "2 doses" },
      { drug: "Ear cleanser (Epi-Otic)", doseMgPerKg: null, doseText: "Flush ear", route: "Topical", frequency: "Every 3 days", duration: "14 days" },
    ],
    supportiveCare: [
      "Treat all in-contact pets simultaneously.",
      "Recheck ear canal at day 30.",
    ],
  },
];
